import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
} from "antd";
import { CloseOutlined, SearchOutlined } from "@ant-design/icons";
import { FilterType } from "types/filtersGenerator.types";
import { Value } from "types/global.types";
import { uniqBy } from "lodash";
import getAuthority from "utils/getAuthority";
import { Roles } from "types/user.types";
import {
  AutoCompleteValues,
  MasterdataSearchParams,
} from "types/masterdata.types";
import styles from "./FiltersGenerator.module.less";

interface Filter<T> {
  type: FilterType;
  name: keyof T;
  label: string;
  initialValue?: Value;
  options?: Record<string, string>[];
  isLoading?: boolean;
  isError?: boolean;
}

interface Props<T> {
  isLoading: boolean;
  searchQuery: MasterdataSearchParams;
  setSearchQuery(queryValues: T): void;
  clearTrigger: number;
  clearFunction(value: number): void;
  rerenderSearch: number;
  setRerenderSearch(value: number): void;
  rekStatusValues: Record<string, number>;
  rekSubStatusValues: Record<string, number>;
  treuhandstelleData?: AutoCompleteValues;
  treuhandstelleIsLoading?: boolean;
  treuhandstelleIsError?: boolean;
}

function FiltersGenerator<T extends Record<string, Value>>({
  isLoading,
  searchQuery,
  setSearchQuery,
  clearTrigger,
  clearFunction,
  rerenderSearch,
  setRerenderSearch,
  rekStatusValues,
  rekSubStatusValues,
  treuhandstelleData,
  treuhandstelleIsLoading,
  treuhandstelleIsError,
}: Props<T>) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const userRoles = getAuthority();

  const filters = [
    {
      type: FilterType.Number,
      name: "kundensatzNr",
      label: t("masterdata.filters.labels.kundensatzNr"),
      initialValue: searchQuery.kundensatzNr,
      authority: [
        Roles.LinkAdmin,
        Roles.LinkAgent,
        Roles.LinkPL,
        Roles.LinkFeldleitung,
        Roles.AgroscopePL,
        Roles.FachlicheHotline,
      ],
    },
    {
      type: FilterType.Text,
      name: "landwirtFirstname",
      label: t("masterdata.filters.labels.landwirtFirstname"),
      initialValue: searchQuery.landwirtFirstname,
      authority: [
        Roles.LinkAdmin,
        Roles.LinkAgent,
        Roles.LinkPL,
        Roles.LinkFeldleitung,
        Roles.AgroscopePL,
        Roles.FachlicheHotline,
      ],
    },
    {
      type: FilterType.Text,
      name: "landwirtLastname",
      label: t("masterdata.filters.labels.landwirtLastname"),
      initialValue: searchQuery.landwirtLastname,
      authority: [
        Roles.LinkAdmin,
        Roles.LinkAgent,
        Roles.LinkPL,
        Roles.LinkFeldleitung,
        Roles.AgroscopePL,
        Roles.FachlicheHotline,
      ],
    },
    {
      type: FilterType.AutoComplete,
      name: "treuhandstelle",
      label: t("masterdata.filters.labels.treuhandstelle"),
      initialValue: searchQuery.treuhandstelle,
      authority: [
        Roles.LinkAdmin,
        Roles.LinkAgent,
        Roles.LinkPL,
        Roles.LinkFeldleitung,
        Roles.AgroscopePL,
        Roles.FachlicheHotline,
      ],
      options: uniqBy(treuhandstelleData, "name")?.map((item) => ({
        label: item.name,
        value: item.name,
      })),
      isLoading: treuhandstelleIsLoading,
      isError: treuhandstelleIsError,
    },
    {
      type: FilterType.Text,
      name: "treuhaender",
      label: t("masterdata.filters.labels.treuhaender"),
      initialValue: searchQuery.treuhaender,
      authority: [
        Roles.LinkAdmin,
        Roles.LinkAgent,
        Roles.LinkPL,
        Roles.LinkFeldleitung,
        Roles.AgroscopePL,
        Roles.FachlicheHotline,
      ],
    },
    {
      type: FilterType.Text,
      name: "betrieb",
      label: t("masterdata.filters.labels.betrieb"),
      initialValue: searchQuery.betrieb,
      authority: [
        Roles.LinkAdmin,
        Roles.LinkAgent,
        Roles.LinkPL,
        Roles.LinkFeldleitung,
        Roles.AgroscopePL,
        Roles.FachlicheHotline,
      ],
    },
    {
      type: FilterType.Text,
      name: "address",
      label: t("masterdata.filters.labels.address"),
      initialValue: searchQuery.address,
      authority: [
        Roles.LinkAdmin,
        Roles.LinkPL,
        Roles.LinkFeldleitung,
        Roles.AgroscopePL,
        Roles.FachlicheHotline,
      ],
    },
    {
      type: FilterType.Text,
      name: "postalCode",
      label: t("masterdata.filters.labels.postalCode"),
      initialValue: searchQuery.postalCode,
      authority: [
        Roles.LinkAdmin,
        Roles.LinkPL,
        Roles.LinkFeldleitung,
        Roles.AgroscopePL,
        Roles.FachlicheHotline,
      ],
    },
    {
      type: FilterType.Text,
      name: "place",
      label: t("masterdata.filters.labels.place"),
      initialValue: searchQuery.place,
      authority: [
        Roles.LinkAdmin,
        Roles.LinkPL,
        Roles.LinkFeldleitung,
        Roles.AgroscopePL,
        Roles.FachlicheHotline,
      ],
    },
    {
      type: FilterType.Text,
      name: "phone",
      label: t("masterdata.filters.labels.phone"),
      initialValue: searchQuery.phone,
      authority: [
        Roles.LinkAdmin,
        Roles.LinkPL,
        Roles.LinkFeldleitung,
        Roles.AgroscopePL,
        Roles.FachlicheHotline,
      ],
    },
    {
      type: FilterType.Text,
      name: "email",
      label: t("masterdata.filters.labels.email"),
      initialValue: searchQuery.email,
      authority: [
        Roles.LinkAdmin,
        Roles.LinkPL,
        Roles.LinkFeldleitung,
        Roles.AgroscopePL,
        Roles.FachlicheHotline,
      ],
    },
    {
      type: FilterType.AutoComplete,
      name: "rekStatus",
      label: t("masterdata.table.labels.rekStatus"),
      initialValue: searchQuery.rekStatus,
      authority: [
        Roles.LinkAdmin,
        Roles.LinkPL,
        Roles.LinkFeldleitung,
        Roles.AgroscopePL,
        Roles.FachlicheHotline,
      ],
      options: Object.keys(rekStatusValues)?.map((key) => ({
        label: t(`masterdata.enums.RekStatusEnum.${rekStatusValues[key]}`),
        value: key,
      })),
    },
    {
      type: FilterType.AutoComplete,
      name: "rekSubStatus",
      label: t("masterdata.table.labels.rekSubStatus"),
      initialValue: searchQuery.rekSubStatus,
      authority: [
        Roles.LinkAdmin,
        Roles.LinkPL,
        Roles.LinkFeldleitung,
        Roles.AgroscopePL,
        Roles.FachlicheHotline,
      ],
      options: Object.keys(rekSubStatusValues)?.map((key) => ({
        label: t(
          `masterdata.enums.RekSubStatusEnum.${rekSubStatusValues[key]}`
        ),
        value: key,
      })),
    },
    {
      type: FilterType.Empty,
      name: "empty1",
      label: "",
      initialValue: null,
    },
    {
      type: FilterType.Empty,
      name: "empty2",
      label: "",
      initialValue: null,
    },
    {
      type: FilterType.Boolean,
      name: "brm",
      label: t("masterdata.filters.labels.brm"),
      initialValue: searchQuery.brm,
      authority: [
        Roles.LinkAdmin,
        Roles.LinkPL,
        Roles.LinkFeldleitung,
        Roles.AgroscopePL,
        Roles.FachlicheHotline,
      ],
    },
    {
      type: FilterType.Boolean,
      name: "abgeschlossenBl",
      label: t("masterdata.filters.labels.abgeschlossenBl"),
      initialValue: searchQuery.abgeschlossenBl,
      authority: [
        Roles.LinkAdmin,
        Roles.LinkPL,
        Roles.LinkFeldleitung,
        Roles.AgroscopePL,
        Roles.FachlicheHotline,
      ],
    },
    {
      type: FilterType.Boolean,
      name: "abgeschlossenThLw",
      label: t("masterdata.filters.labels.abgeschlossenThLw"),
      initialValue: searchQuery.abgeschlossenThLw,
      authority: [
        Roles.LinkAdmin,
        Roles.LinkPL,
        Roles.LinkFeldleitung,
        Roles.AgroscopePL,
        Roles.FachlicheHotline,
      ],
    },
    {
      type: FilterType.Boolean,
      name: "entschaedigung",
      label: t("masterdata.filters.labels.entschaedigung"),
      initialValue: searchQuery.entschaedigung,
      authority: [
        Roles.LinkAdmin,
        Roles.LinkPL,
        Roles.LinkFeldleitung,
        Roles.AgroscopePL,
        Roles.FachlicheHotline,
      ],
    },
  ];

  const initialValues = useMemo(
    () =>
      filters.reduce<T>((acc, curr) => {
        acc = {
          ...acc,
          [curr.name]: curr.initialValue || "",
        };
        return acc;
      }, {} as T),
    []
  );

  const clearForm = () => {
    form.resetFields();
    form.setFieldsValue(
      filters.reduce((acc, curr) => {
        acc = { ...acc, [curr.name]: "" };
        return acc;
      }, {} as T)
    );
    const searchQueryValues = filters.reduce((acc, curr) => {
      acc = {
        ...acc,
        [curr.name]: undefined,
      };
      return acc;
    }, {} as T);
    setSearchQuery(searchQueryValues);
    clearFunction(clearTrigger + 1);
  };

  const handleSubmit = (values: T) => {
    const searchQueryValues = filters.reduce((acc, curr) => {
      acc = {
        ...acc,
        [curr.name]: values[curr.name] || undefined,
      };
      return acc;
    }, {} as T);
    setRerenderSearch(rerenderSearch + 1);
    setSearchQuery(searchQueryValues);
  };

  const renderInput = ({
    type,
    props,
  }: {
    type: FilterType;
    props: {
      disabled: boolean;
      options?: Record<string, string>[];
      isError?: boolean;
    };
  }) => {
    switch (type) {
      case FilterType.Text:
        return <Input disabled={props.disabled} />;
      case FilterType.Number:
        return (
          <InputNumber
            className={styles.input__full_width}
            disabled={props.disabled}
            min={0}
          />
        );
      case FilterType.AutoComplete:
        return (
          <Select
            allowClear
            notFoundContent={null}
            showSearch
            options={props.options}
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "")
                ?.toLowerCase()
                .includes(input?.toLowerCase())
            }
            filterSort={(optionA, optionB) => {
              const getNumber = (label: string) => {
                const match = label.match(/-?\d+/);
                return match ? parseInt(match[0], 10) : 0;
              };

              const numberA = getNumber(optionA?.label ?? "");
              const numberB = getNumber(optionB?.label ?? "");

              const diff = numberA - numberB;
              return diff !== 0
                ? diff
                : (optionA?.label ?? "")
                    ?.toLowerCase()
                    .localeCompare((optionB?.label ?? "").toLowerCase());
            }}
            {...(props.isError && { status: "error" })}
          />
        );
      case FilterType.Boolean:
        return (
          <Radio.Group>
            <Radio value="true">{t("yes")}</Radio>
            <Radio value="false">{t("no")}</Radio>
          </Radio.Group>
        );
      case FilterType.Radio:
        return (
          <Radio.Group>
            {props.options?.map(({ label, value }) => (
              <Radio key={`${label} ${value}`} value={value}>
                {label}
              </Radio>
            ))}
          </Radio.Group>
        );
      default:
        return <Input disabled={props.disabled} />;
    }
  };

  const renderItem = (filter: Filter<T>) =>
    filter.type === FilterType.Empty ? (
      <Col span={6} />
    ) : (
      <Col key={String(filter.name)} span={6}>
        <Form.Item label={filter.label} name={String(filter.name)}>
          {renderInput({
            type: filter.type,
            props: {
              disabled: isLoading || !!filter.isLoading,
              options: filter.options,
              isError: filter.isError,
            },
          })}
        </Form.Item>
      </Col>
    );
  const filterFields = filters.map((filter, index) => {
    const userHasRequiredRole =
      userRoles && filter.authority
        ? filter.authority.some((el) => userRoles.indexOf(el) !== -1)
        : true;
    if (index === 11) {
      return (
        <React.Fragment key={String(filter.name)}>
          <Divider />
          {userHasRequiredRole ? renderItem(filter) : null}
        </React.Fragment>
      );
    }
    return userHasRequiredRole ? renderItem(filter) : null;
  });

  return (
    <Form
      initialValues={initialValues}
      form={form}
      onFinish={handleSubmit}
      layout="vertical"
    >
      <Row justify="space-between" gutter={[20, 20]}>
        <Col span={19}>
          <Row gutter={[20, 0]}>{filterFields}</Row>
        </Col>
        <Col>
          <Divider type="vertical" style={{ height: "100%" }} />
        </Col>
        <Col span={4}>
          <Row gutter={[10, 10]}>
            <Col span={24}>
              <Button
                loading={isLoading}
                icon={<SearchOutlined />}
                htmlType="submit"
                type="primary"
                size="large"
                block
              >
                {t("search")}
              </Button>
            </Col>
            <Col span={24}>
              <Button
                disabled={isLoading}
                icon={<CloseOutlined />}
                onClick={clearForm}
                block
              >
                {t("clear")}
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
}

export default FiltersGenerator;
